import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMediaQuery } from '@/lib/hooks';
import DocPreviewPanel from './DocPreviewPanel';
import {
    IconCheck, IconAlertCircle, IconPencil, IconDownload, IconPrinter,
    IconLanguage, IconTrash, IconFileDownload, IconX, IconShare, IconEye,
    IconForms, IconDeviceFloppy, IconBan, IconHighlight, IconHighlightOff, IconAward, IconEdit
} from '@tabler/icons-react';
import { lexicalToHtml, replacePlaceholders } from '../../utils/lexicalToHtml';
import { numberToBengaliWords } from '../../utils/currencyWords';
import { generateDocPdf, downloadBlob, printBlob, shareBlobs, resolvePageSettings } from '../../utils/generateDocPdf';
import PdfEditorModal from '../../features/pdf-editor/PdfEditorModal';
import { manualHtmlToDoc } from '../../features/pdf-editor/engine/manualDocToPdf';
import { objectLayoutToPdf, serializeEditorLayout } from '../../features/pdf-editor/engine/docToPdf';
import { usePdfEditorStore } from '../../features/pdf-editor/state/usePdfEditorStore';
import TableBuilderInput from './TableBuilderInput';
import ListBuilderInput from './ListBuilderInput';
import Form, { Field, useForm } from 'rc-field-form';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import generatedDocService from '../../services/generatedDocService';
import TooltipButton from '../common/TooltipButton';
import TipBtn from '../common/TipBtn';
import SelectField from '../common/SelectField';
import DatePickerField from '../common/DatePickerField';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { useAuthStore } from '../../store/authStore';
import { useIncomeExpenseStore } from '../../store/incomeExpenseStore';
import { moduleAutoFill } from '../../lib/incomeExpenseDefaults';
import LimitReachedModal from '../common/LimitReachedModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { sanitizeDocHtml } from '../../utils/sanitizeDocHtml';

// ─── LocalStorage draft helpers ───────────────────────────────────────────────
const DRAFT_KEY_PREFIX = 'generateDocDraft_';

const getDraftKey = (templateSetId, documentGroupId) => {
    const id = documentGroupId || templateSetId;
    return id ? `${DRAFT_KEY_PREFIX}${id}` : null;
};

const saveFormDraft = (formData, templateSetId, documentGroupId) => {
    const key = getDraftKey(templateSetId, documentGroupId);
    if (!key) return false;
    try {
        localStorage.setItem(key, JSON.stringify({ ...formData, timestamp: new Date().toISOString() }));
        return true;
    } catch (error) {
        console.error('Failed to save form draft:', error);
        return false;
    }
};

const loadFormDraft = (templateSetId, documentGroupId) => {
    const key = getDraftKey(templateSetId, documentGroupId);
    if (!key) return null;
    try {
        const saved = localStorage.getItem(key);
        if (!saved) return null;
        const data = JSON.parse(saved);
        return data && typeof data === 'object' ? data : null;
    } catch { return null; }
};

const clearFormDraft = (templateSetId, documentGroupId) => {
    const key = getDraftKey(templateSetId, documentGroupId);
    if (!key) return false;
    try { localStorage.removeItem(key); return true; } catch { return false; }
};

// ─── Manual edit localStorage helpers ─────────────────────────────────────────
const MANUAL_EDIT_MODE_PREFIX = 'manualEditMode_';
const MANUAL_EDIT_CONTENT_PREFIX = 'manualEditContent_';

const getManualEditModeKey = (id) => id ? `${MANUAL_EDIT_MODE_PREFIX}${id}` : null;
const getManualEditContentKey = (tsId, docId) =>
    tsId && docId ? `${MANUAL_EDIT_CONTENT_PREFIX}${tsId}_${docId}` : null;

const saveManualEditMode = (id, enabled) => {
    const key = getManualEditModeKey(id);
    if (!key) return;
    try { if (enabled) localStorage.setItem(key, '1'); else localStorage.removeItem(key); } catch (_) {}
};

const loadManualEditMode = (id) => {
    const key = getManualEditModeKey(id);
    if (!key) return false;
    try { return localStorage.getItem(key) === '1'; } catch { return false; }
};

const saveManualEditContent = (tsId, docId, html) => {
    const key = getManualEditContentKey(tsId, docId);
    if (!key) return;
    try { localStorage.setItem(key, html); } catch (_) {}
};

const loadManualEditContent = (tsId, docId) => {
    const key = getManualEditContentKey(tsId, docId);
    if (!key) return null;
    try { return localStorage.getItem(key); } catch { return null; }
};

const clearManualEditData = (tsId, templateDocIds = []) => {
    try {
        localStorage.removeItem(getManualEditModeKey(tsId));
        templateDocIds.forEach(id => localStorage.removeItem(getManualEditContentKey(tsId, id)));
    } catch (_) {}
};

// ─── Predefined option lists ──────────────────────────────────────────────────
const PREDEFINED_TYPE_OPTIONS = {
    Occupation: ['ব্যবসায়ী', 'চাকরিজীবী', 'কৃষক', 'শিক্ষার্থী', 'গৃহিণী', 'শিক্ষক', 'ডাক্তার', 'প্রকৌশলী', 'আইনজীবী', 'অবসরপ্রাপ্ত'],
    Nationality: ['বাংলাদেশী', 'ভারতীয়', 'পাকিস্তানি'],
    Religion: ['ইসলাম', 'হিন্দু', 'খ্রিস্টান', 'বৌদ্ধ'],
};
const PREDEFINED_TYPES = new Set(Object.keys(PREDEFINED_TYPE_OPTIONS));
const LAND_UNITS = ['কাঠা', 'শতক', 'বিঘা', 'একর', 'শতাংশ', 'কানি'];
const LAND_UNITS_SET = new Set(LAND_UNITS);

const BN_EN_DIGIT_RE = /[^0-9০-৯.\- +]/g;
const sanitizeBnEnNumber = (raw) => (raw ?? '').replace(BN_EN_DIGIT_RE, '');

const formatNumberWithCommas = (raw) => {
    if (!raw) return '';
    const str = String(raw);
    const hasBangla = /[০-৯]/.test(str);
    const en = str.replace(/[০-৯]/g, (d) => '০১২৩৪৫৬৭৮৯'.indexOf(d).toString());
    const stripped = en.replace(/,/g, '');
    const [intPart, decPart] = stripped.split('.');
    const digits = (intPart || '').replace(/\D/g, '');
    if (!digits) return raw;
    const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const result = decPart !== undefined ? `${formatted}.${decPart}` : formatted;
    const BN = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
    return hasBangla ? result.replace(/[0-9]/g, (d) => BN[d]) : result;
};

const EN_TO_BN_DIGIT = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
const toBanglaDigits = (s) => String(s ?? '').replace(/[0-9]/g, (d) => EN_TO_BN_DIGIT[d]);
const toEnglishDigits = (s) => String(s ?? '').replace(/[০-৯]/g, (d) => '০১২৩৪৫৬৭৮৯'.indexOf(d));

const BN_DAYS = ['রবিবার', 'সোমবার', 'মঙ্গলবার', 'বুধবার', 'বৃহস্পতিবার', 'শুক্রবার', 'শনিবার'];
const EN_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// The document language ('bangla' | 'english' | 'mixed') decides whether dates
// are stamped in Bangla or English. Only an explicitly English document opts out
// of the Bangla numerals/weekday; 'mixed' stays Bangla like before.
const isEnglishDoc = (language) => language === 'english';

const parseBnDateString = (value) => {
    if (!value) return null;
    const en = toEnglishDigits(value);
    const m = en.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return isNaN(d.getTime()) ? null : d;
};

const formatDocDate = (date, language) => {
    if (!date) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = String(date.getFullYear());
    const s = `${dd}/${mm}/${yyyy}`;
    return isEnglishDoc(language) ? s : toBanglaDigits(s);
};

function applyDocTransforms(rawValues, placeholders, wordsEnabled, landUnitWordsEnabled = {}, language) {
    if (!placeholders || !placeholders.length) return rawValues;
    const result = { ...rawValues };
    placeholders.forEach((p) => {
        if (p.type === 'Currency' && result[p.slug]) {
            const formatted = formatNumberWithCommas(result[p.slug]);
            if (wordsEnabled[p.slug] !== false) {
                const words = numberToBengaliWords(result[p.slug]);
                result[p.slug] = words ? `${formatted} (${words})` : `${formatted} টাকা`;
            } else {
                result[p.slug] = `${formatted} টাকা`;
            }
        }
        if (p.type === 'LandUnit' && result[p.slug] && landUnitWordsEnabled[p.slug] !== false) {
            const fullVal = result[p.slug];
            const parts = fullVal.split(' ');
            const lastPart = parts[parts.length - 1];
            const hasUnit = LAND_UNITS_SET.has(lastPart);
            const amount = hasUnit ? parts.slice(0, -1).join(' ') : fullVal;
            const unit = hasUnit ? lastPart : '';
            const words = amount ? numberToBengaliWords(amount) : '';
            if (words) result[p.slug] = unit ? `${amount} (${words}) ${unit}` : `${amount} (${words})`;
        }
        if (p.type === 'DateBar' && result[p.slug]) {
            const date = parseBnDateString(result[p.slug]);
            if (date) {
                // Re-render from the parsed date so digits match the doc language
                // even if the stored value was captured before the language was set.
                const base = formatDocDate(date, language);
                result[p.slug] = isEnglishDoc(language)
                    ? `${base}, ${EN_DAYS[date.getDay()]}`
                    : `${base} ইং তারিখ ${BN_DAYS[date.getDay()]}`;
            }
        }
    });
    return result;
}

// ─── Small reusable UI primitives ─────────────────────────────────────────────
function FieldLabel({ children, description, error }) {
    return (
        <div className="flex flex-col gap-0.5 mb-1">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{children}</span>
            {description && <span className="text-xs text-gray-400 dark:text-gray-500">{description}</span>}
            {error && <span className="text-xs text-red-500">{error}</span>}
        </div>
    );
}

const inputClass = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-50";

function TextInput({ label, description, error, ...props }) {
    return (
        <div>
            {label && <FieldLabel description={description} error={error}>{label}</FieldLabel>}
            <input className={inputClass} {...props} />
            {!label && error && <span className="text-xs text-red-500">{error}</span>}
        </div>
    );
}

function Textarea({ label, description, error, minRows = 2, ...props }) {
    return (
        <div>
            {label && <FieldLabel description={description} error={error}>{label}</FieldLabel>}
            <textarea
                rows={minRows}
                className={`${inputClass} resize-none`}
                {...props}
            />
            {!label && error && <span className="text-xs text-red-500">{error}</span>}
        </div>
    );
}

function Btn({ variant = 'primary', size = 'md', leftIcon, rightIcon, loading, disabled, className = '', children, ...props }) {
    const base = 'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:pointer-events-none';
    const sizes = { xs: 'px-2 py-1 text-xs', sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2 text-sm' };
    const variants = {
        primary: 'bg-primary hover:bg-primary/90 text-white focus:ring-primary',
        default: 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-gray-400',
        light: 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-300 hover:bg-primary/20 dark:hover:bg-primary/30 focus:ring-primary/60',
        tealLight: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 hover:bg-teal-100 focus:ring-teal-400',
        violetLight: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 hover:bg-violet-100 focus:ring-violet-400',
        subtle: 'bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:ring-gray-400',
        redSubtle: 'bg-transparent text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 focus:ring-red-400',
        yellowFilled: 'bg-yellow-400 hover:bg-yellow-500 text-yellow-900 focus:ring-yellow-400',
        greenFilled: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
        red: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
    };
    return (
        <button
            className={`${base} ${sizes[size] || sizes.md} ${variants[variant] || variants.primary} ${className}`}
            disabled={disabled || loading}
            {...props}
        >
            {loading ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : leftIcon}
            {children}
            {rightIcon}
        </button>
    );
}

function Switch({ checked, onChange, label, size = 'md', disabled }) {
    const track = checked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600';
    const knob = checked ? (size === 'xs' ? 'translate-x-[14px]' : 'translate-x-[18px]') : 'translate-x-0.5';
    const trackW = size === 'xs' ? 'w-7 h-4' : 'w-9 h-5';
    const knobW = size === 'xs' ? 'w-3 h-3' : 'w-4 h-4';
    return (
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => onChange?.({ currentTarget: { checked: !checked } })}
                className={`relative inline-flex shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${trackW} ${track} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <span className={`inline-block ${knobW} bg-white rounded-full shadow-sm transform transition-transform mt-0.5 ${knob}`} />
            </button>
            {label && <span className="text-xs text-gray-600 dark:text-gray-300">{label}</span>}
        </label>
    );
}

function RadioGroup({ label, description, error, value, onChange, children }) {
    return (
        <div>
            {label && <FieldLabel description={description} error={error}>{label}</FieldLabel>}
            <div className="flex flex-wrap gap-3 mt-1.5">{children}</div>
        </div>
    );
}

function Radio({ value: radioVal, label, currentValue, onChange }) {
    const checked = currentValue === radioVal;
    return (
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
                type="radio"
                checked={checked}
                onChange={() => onChange(radioVal)}
                className="w-4 h-4 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">{label}</span>
        </label>
    );
}

function Badge({ children, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                active
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
        >
            {children}
        </button>
    );
}

function Alert({ icon, color = 'blue', title, children }) {
    const colors = {
        green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
        red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
        orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-800 dark:text-orange-200',
        blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
    };
    return (
        <div className={`rounded-md border p-3 ${colors[color] || colors.blue}`}>
            <div className="flex items-start gap-2">
                {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
                <div>
                    {title && <p className="font-semibold text-sm">{title}</p>}
                    {children && <p className="text-sm mt-0.5">{children}</p>}
                </div>
            </div>
        </div>
    );
}

function Modal({ opened, onClose, title, children }) {
    if (!opened) return null;
    return (
        <div className="fixed inset-0 z-[2147483647] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-sm mx-4 p-5">
                {title && <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">{title}</h3>}
                {children}
            </div>
        </div>
    );
}

// ─── Step indicator ────────────────────────────────────────────────────────────
function StepIndicator({ steps, activeStep }) {
    if (!steps || steps.length === 0) return null;
    return (
        <div className="w-full flex items-center py-1 mt-1 mb-0">
            {steps.map((step, i) => {
                const done = i < activeStep;
                const current = i === activeStep;
                const label = step.label.length > 20 ? step.label.substring(0, 20) + '…' : step.label;
                return (
                    <React.Fragment key={i}>
                        <div className="flex flex-col items-center gap-1 shrink-0">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                                done ? 'bg-primary text-white' :
                                current ? 'bg-primary text-white ring-2 ring-primary/30' :
                                'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                            }`}>
                                {done ? <IconCheck size={12} /> : i + 1}
                            </div>
                            <span className={`text-[11px] whitespace-nowrap max-w-[80px] overflow-hidden text-ellipsis text-center ${
                                current ? 'text-primary dark:text-primary-300 font-medium' : 'text-gray-400 dark:text-gray-500'
                            }`}>{label}</span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-2 mb-5 min-w-[12px] ${i < activeStep ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// ─── NativeSelect (replaces Mantine Select for simple dropdowns) ───────────────
function NativeSelect({ label, description, error, value, onChange, data, placeholder }) {
    return (
        <div>
            {label && <FieldLabel description={description} error={error}>{label}</FieldLabel>}
            <select
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                className={inputClass}
            >
                {placeholder && <option value="">{placeholder}</option>}
                {data?.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        </div>
    );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function GenerateDocDrawer({ opened, onClose, templateSet = null, documentGroup = null, onSuccess }) {
    const { t } = useTranslation();
    const { selectedOfficeSpace } = useOfficeSpaceStore();
    const { user, updateSettings } = useAuthStore();
    const { openModal: openIncomeExpenseModal } = useIncomeExpenseStore();
    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const isOperator = !!(selectedOfficeSpace && user && selectedOfficeSpace.ownerId !== user.id);

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [generatedDocs, setGeneratedDocs] = useState(null);
    const [generatedBlobs, setGeneratedBlobs] = useState({});
    const [form] = useForm();
    const [dynamicSteps, setDynamicSteps] = useState([]);
    const [currencyWordsEnabled, setCurrencyWordsEnabled] = useState({});
    const [landUnitWordsEnabled, setLandUnitWordsEnabled] = useState({});

    const documentNameRef = useRef(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editingNameValue, setEditingNameValue] = useState('');
    const previewPageRef = useRef(null);

    const R2_URL = import.meta.env.VITE_R2_PUBLIC_URL || '';

    const PAGE_SIZES_EXPORT = {
        A4:     { width: 794,  height: 1123, jsPdf: 'a4'     },
        A3:     { width: 1123, height: 1587, jsPdf: 'a3'     },
        A5:     { width: 559,  height: 794,  jsPdf: 'a5'     },
        LETTER: { width: 816,  height: 1056, jsPdf: 'letter' },
        LEGAL:  { width: 816,  height: 1344, jsPdf: 'legal'  },
    };

    const toBnDigits = n => String(n).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[d]);
    const PAGE_NUM_EXPORT_STYLE = `position:absolute;left:0;right:0;text-align:center;font-family:'Kalpurush','Noto Sans Bengali','SolaimanLipi',Arial,sans-serif;font-size:14px;color:#222;line-height:1.4;`;

    const buildExportHtml = (rawPages, pagePadding, pageHeight, margin, isStamp = false) =>
        rawPages.map((p, i) => {
            const isFirst = i === 0;
            const isLast  = i === rawPages.length - 1;
            const wrapStyle  = `${pagePadding}position:relative;height:${pageHeight}px;overflow:hidden;display:block;`;
            const showNums   = isStamp && rawPages.length > 1;
            const topNum    = showNums && !isFirst ? `<div style="${PAGE_NUM_EXPORT_STYLE}top:${Math.max(8, margin.top - 28)}px;">পাতা নং-${toBnDigits(i + 1)}</div>` : '';
            const bottomNum = showNums && !isLast  ? `<div style="${PAGE_NUM_EXPORT_STYLE}bottom:${Math.max(8, margin.bottom - 28)}px;">চলমান-${toBnDigits(i + 2)}</div>` : '';
            return `<div style="${wrapStyle}">${topNum}${p}${bottomNum}</div>`;
        }).join('');

    const PAGE_MARGINS_EXPORT = {
        narrow:   { top: 48,  bottom: 48,  left: 36, right: 36 },
        normal:   { top: 72,  bottom: 72,  left: 72, right: 72 },
        moderate: { top: 60,  bottom: 60,  left: 54, right: 54 },
        wide:     { top: 96,  bottom: 96,  left: 96, right: 96 },
        stamp:    { top: 216, bottom: 72,  left: 18, right: 18 },
    };

    const resolvePageSettings = (doc) => {
        const md = doc?.metadata || {};
        const pageSize = PAGE_SIZES_EXPORT[String(md.pageSize || 'A4').toUpperCase()] || PAGE_SIZES_EXPORT.A4;
        const legacy = PAGE_MARGINS_EXPORT[String(md.margin || 'normal').toLowerCase()] || PAGE_MARGINS_EXPORT.normal;
        const margin = {
            top:    typeof md.marginTop    === 'number' ? md.marginTop    : legacy.top,
            bottom: typeof md.marginBottom === 'number' ? md.marginBottom : legacy.bottom,
            left:   typeof md.marginLeft   === 'number' ? md.marginLeft   : legacy.left,
            right:  typeof md.marginRight  === 'number' ? md.marginRight  : legacy.right,
        };
        return { pageSize, margin };
    };

    const [unicodeModalOpened, setUnicodeModalOpened] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [hasDraft, setHasDraft] = useState(false);
    const saveTimeoutRef = useRef(null);
    const initialFormValuesRef = useRef(null);

    const isRegenerating = !!documentGroup;

    const [previewOpen, setPreviewOpen] = useState(true);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isNarrowDesktop = useMediaQuery('(max-width: 1100px)');
    const [mobilePanelView, setMobilePanelView] = useState('form');
    const [stampVisible, setStampVisible] = useState(() => user?.settings?.stampVisible !== false);
    const [stampDenomination, setStampDenomination] = useState(50);
    const [isEditMode, setIsEditMode] = useState(false);

    const [isManualEditMode, setIsManualEditMode] = useState(false);
    const [manualEditConfirmOpen, setManualEditConfirmOpen] = useState(false);
    const [destroyManualConfirmOpen, setDestroyManualConfirmOpen] = useState(false);
    const [manualHtmlMap, setManualHtmlMap] = useState({});
    const manualHtmlMapRef = useRef({});
    const [showPlaceholderHighlights, setShowPlaceholderHighlights] = useState(true);


    const [activeDocIndex, setActiveDocIndex] = useState(0);
    const [excludedDocIds, setExcludedDocIds] = useState(new Set());
    const [liveFormValues, setLiveFormValues] = useState({});
    const [activePlaceholderSlug, setActivePlaceholderSlug] = useState(null);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    // Document editor (the shared PDF editor in document mode) opened from the generated step.
    // `editor` = { doc, file, objects, pageMargins } while open; edits per template doc survive a close.
    const [docEditor, setDocEditor] = useState(null);
    const [docEditorBusyId, setDocEditorBusyId] = useState(null);
    const docEditorLayoutsRef = useRef({});

    const isOnLastStep = dynamicSteps.length === 0 || activeStep === dynamicSteps.length - 1;

    useEffect(() => {
        if (!opened) {
            form.resetFields();
            setActiveStep(0);
            setGeneratedDocs(null);
            setGeneratedBlobs({});
            setHasUnsavedChanges(false);
            setHasDraft(false);
            setPreviewOpen(false);
            setLiveFormValues({});
            setActivePlaceholderSlug(null);
            initialFormValuesRef.current = null;
            setIsManualEditMode(false);
            setManualHtmlMap({});
            manualHtmlMapRef.current = {};
            setExcludedDocIds(new Set());
        } else {
            setHasUnsavedChanges(false);
            setPreviewOpen(true);

            const currentTemplateSet = templateSet || documentGroup?.templateSet;
            const templateSetId = currentTemplateSet?.id;
            const documentGroupId = documentGroup?.id;

            const isDbManuallyEdited = documentGroup?.generatedDocs?.some(d => d.metadata?.isManuallyEdited === true);
            const isLsManualMode = loadManualEditMode(templateSetId);

            if (isDbManuallyEdited || isLsManualMode) {
                setIsManualEditMode(true);
                setIsEditMode(true);

                const templateDocs = currentTemplateSet?.docs || [];
                const initialMap = {};
                templateDocs.forEach(doc => {
                    const lsContent = loadManualEditContent(templateSetId, doc.id);
                    if (lsContent !== null) {
                        initialMap[doc.id] = lsContent;
                    } else if (isDbManuallyEdited) {
                        const genDoc = documentGroup?.generatedDocs?.find(
                            gd => gd.title?.includes(doc.title) || gd.id
                        );
                        if (genDoc?.content) initialMap[doc.id] = sanitizeDocHtml(genDoc.content);
                    }
                });
                setManualHtmlMap(initialMap);
                manualHtmlMapRef.current = initialMap;

                const hasRestoredLs = Object.keys(initialMap).length > 0 && isLsManualMode;
                if (hasRestoredLs) {
                    notifications.show({
                        id: 'manual-edit-restored',
                        title: t('documents.manualEditBadge'),
                        message: t('documents.manualEditResumed'),
                        color: 'blue',
                        autoClose: 4000
                    });
                }
                initializeForm();
            } else {
                const draft = loadFormDraft(templateSetId, documentGroupId);
                if (draft && draft.timestamp) {
                    form.setFieldsValue(draft);
                    setLiveFormValues(draft);
                    setHasDraft(true);
                    initialFormValuesRef.current = JSON.stringify(draft);
                    notifications.show({
                        id: 'draft-restored',
                        title: 'ড্রাফট পুনরুদ্ধার হয়েছে',
                        message: 'আপনার আগের ডেটা পুনরুদ্ধার করা হয়েছে',
                        color: 'blue',
                        autoClose: 3000
                    });
                } else {
                    setHasDraft(false);
                    initializeForm();
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, isRegenerating, documentGroup, templateSet]);

    useEffect(() => {
        if (!isManualEditMode) return;
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        const templateSetId = currentTemplateSet?.id;
        if (!templateSetId) return;
        const handleBeforeUnload = () => {
            const map = manualHtmlMapRef.current;
            Object.entries(map).forEach(([docId, html]) => saveManualEditContent(templateSetId, docId, html));
            saveManualEditMode(templateSetId, true);
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isManualEditMode, templateSet, documentGroup]);

    const initializeForm = () => {
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        const initialValues = { documentName: currentTemplateSet?.name || 'Untitled Document' };

        if (currentTemplateSet?.placeholders) {
            currentTemplateSet.placeholders.forEach(placeholder => {
                if (placeholder.type === 'Steps' || placeholder.type === 'Separator') return;
                if (placeholder.type === 'TableBuilder') {
                    initialValues[placeholder.slug] = { headers: [], rows: [], orientation: placeholder.orientation || 'horizontal' };
                } else if (placeholder.type === 'Increment' || placeholder.type === 'BulletList') {
                    initialValues[placeholder.slug] = { items: [], listType: placeholder.type };
                } else if (placeholder.type === 'Insert' && placeholder.description) {
                    initialValues[placeholder.slug] = placeholder.description;
                    initialValues[`${placeholder.slug}_enabled`] = true;
                } else if (PREDEFINED_TYPES.has(placeholder.type)) {
                    initialValues[placeholder.slug] = PREDEFINED_TYPE_OPTIONS[placeholder.type][0] || '';
                } else if ((placeholder.type === 'Options' || placeholder.type === 'CustomToggle') && placeholder.options) {
                    try {
                        const firstOpt = placeholder.options.startsWith('[')
                            ? (JSON.parse(placeholder.options)[0] || '')
                            : (placeholder.options.split(',')[0]?.trim() || '');
                        initialValues[placeholder.slug] = firstOpt;
                    } catch (_) { initialValues[placeholder.slug] = ''; }
                } else {
                    const noDropdownTypes = new Set(['YesNo', 'Date', 'DateBar', 'Currency', 'Insert']);
                    let firstOpt = '';
                    if (placeholder.options && !noDropdownTypes.has(placeholder.type)) {
                        try {
                            firstOpt = placeholder.options.startsWith('[')
                                ? (JSON.parse(placeholder.options)[0] || '')
                                : (placeholder.options.split(',')[0]?.trim() || '');
                        } catch (_) {}
                    }
                    initialValues[placeholder.slug] = firstOpt;
                    if (placeholder.type === 'Insert') initialValues[`${placeholder.slug}_enabled`] = false;
                }
            });
        }

        if (isRegenerating && documentGroup) {
            if (documentGroup.placeholderValues && Object.keys(documentGroup.placeholderValues).length > 0) {
                initialValues.documentName = documentGroup.title || '';
                Object.keys(documentGroup.placeholderValues).forEach(key => {
                    const value = documentGroup.placeholderValues[key] || '';
                    initialValues[key] = value;
                    const placeholder = currentTemplateSet?.placeholders?.find(p => p.slug === key);
                    if (placeholder && placeholder.type === 'Insert') {
                        initialValues[`${key}_enabled`] = value.trim() !== '';
                    }
                });
            } else {
                initialValues.documentName = documentGroup.title || 'Untitled Document';
            }
        }

        form.setFieldsValue(initialValues);
        setLiveFormValues(initialValues);
        initialFormValuesRef.current = JSON.stringify(initialValues);
    };

    const handleClearDraft = () => {
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        clearFormDraft(currentTemplateSet?.id, documentGroup?.id);
        initializeForm();
        setHasDraft(false);
        setHasUnsavedChanges(false);
        notifications.show({ title: 'ড্রাফট মুছে ফেলা হয়েছে', message: 'ফর্ম রিসেট করা হয়েছে', color: 'blue', autoClose: 2000 });
    };

    const handleManualContentChange = (templateDocId, html) => {
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        const templateSetId = currentTemplateSet?.id;
        const updated = { ...manualHtmlMapRef.current, [templateDocId]: html };
        manualHtmlMapRef.current = updated;
        setManualHtmlMap(updated);
        saveManualEditContent(templateSetId, templateDocId, html);
    };

    const handleConfirmManualEdit = async () => {
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        const templateSetId = currentTemplateSet?.id;
        const docs = currentTemplateSet?.docs || [];
        setManualEditConfirmOpen(false);
        setLoading(true);
        try {
            const prerenderedMap = {};
            await Promise.all(
                docs.map(async (doc) => {
                    if (!doc.contentKey) return;
                    try {
                        const res = await fetch(`${R2_URL}/${doc.contentKey}?t=${Date.now()}`);
                        if (!res.ok) return;
                        const json = await res.json();
                        const rawHtml = lexicalToHtml(json);
                        prerenderedMap[doc.id] = replacePlaceholders(rawHtml, documentValues, placeholdersConfig);
                    } catch (err) { console.error('Failed to pre-render doc', doc.id, err); }
                })
            );
            manualHtmlMapRef.current = prerenderedMap;
            setManualHtmlMap(prerenderedMap);
            Object.entries(prerenderedMap).forEach(([docId, html]) => saveManualEditContent(templateSetId, docId, html));
            setIsManualEditMode(true);
            setIsEditMode(true);
            saveManualEditMode(templateSetId, true);
        } finally { setLoading(false); }
    };

    const handleDestroyManualEdit = () => {
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        const templateSetId = currentTemplateSet?.id;
        const templateDocIds = (currentTemplateSet?.docs || []).map(d => d.id);
        clearManualEditData(templateSetId, templateDocIds);
        setIsManualEditMode(false);
        setIsEditMode(false);
        setManualHtmlMap({});
        manualHtmlMapRef.current = {};
        setDestroyManualConfirmOpen(false);
    };

    useEffect(() => {
        if (!opened || activeStep === 0) return;
        const id = setTimeout(() => {
            const container = document.querySelector(`[data-step-index="${activeStep}"]`);
            if (!container) return;
            const field = container.querySelector('textarea, input');
            if (field) { field.focus(); if (field.select) field.select(); }
        }, 0);
        return () => clearTimeout(id);
    }, [opened, activeStep]);

    const handleBack = () => {
        if (generatedDocs) setGeneratedDocs(null);
        else if (activeStep > 0) setActiveStep(activeStep - 1);
    };

    const handleNext = () => {
        if (activeStep < dynamicSteps.length - 1) setActiveStep(activeStep + 1);
    };

    const handleSeeDocs = async () => {
        if (isRegenerating && documentGroup) {
            setLoading(true);
            try {
                const response = await generatedDocService.getDocSetById(documentGroup.id);
                if (response.success) setGeneratedDocs(response.data);
            } catch (error) {
                notifications.show({ title: t('common.error'), message: t('documents.failedToLoad') || error.message, color: 'red' });
            } finally { setLoading(false); }
        }
    };

    const handleGenerate = async () => {
        if (!isFormComplete) {
            notifications.show({ title: t('common.validationError'), message: t('documents.completeAllFieldsBeforeGenerate'), color: 'red' });
            return;
        }
        try {
            await form.validateFields();
            const values = { ...liveFormValues };
            const docName = (values.documentName || '').trim();
            const currentTemplateSet = templateSet || documentGroup?.templateSet;

            if (!currentTemplateSet) {
                notifications.show({ title: t('common.error'), message: t('documents.templateSetMissing'), color: 'red' });
                return;
            }
            if (!docName || docName.length < 2) {
                notifications.show({ title: t('common.validationError'), message: t('validation.documentNameMin'), color: 'red' });
                return;
            }

            setLoading(true);
            const placeholderValues = {};
            if (currentTemplateSet.placeholders?.length > 0) {
                currentTemplateSet.placeholders.forEach(p => {
                    if (p.type === 'Steps' || p.type === 'Separator') return;
                    const value = values[p.slug];
                    if (p.type === 'TableBuilder') {
                        if (value?.headers && value?.rows && value.rows.length > 0) placeholderValues[p.slug] = value;
                    } else if (p.type === 'Increment' || p.type === 'BulletList') {
                        if (value?.items?.length > 0) {
                            const nonEmpty = value.items.filter(item => item?.trim() !== '');
                            if (nonEmpty.length > 0) placeholderValues[p.slug] = { items: nonEmpty, listType: value.listType || p.type };
                        }
                    } else if (p.type === 'Insert') {
                        if (value?.trim() && values[`${p.slug}_enabled`]) placeholderValues[p.slug] = value.trim();
                    } else {
                        if (value?.trim()) placeholderValues[p.slug] = value.trim();
                    }
                });
            }

            const selectedOfficeSpaceId = selectedOfficeSpace?.id || null;
            let response;
            if (isRegenerating) {
                try {
                    response = await generatedDocService.regenerateDoc(documentGroup.id, placeholderValues, docName, null);
                } catch (error) {
                    if (error.message?.includes('not found') || error.message?.includes('404')) {
                        notifications.show({ title: t('common.info'), message: t('documents.usingGenerateMode'), color: 'blue', autoClose: 3000 });
                        response = await generatedDocService.generateDocument(currentTemplateSet.id, selectedOfficeSpaceId, placeholderValues, docName, null);
                    } else throw error;
                }
            } else {
                response = await generatedDocService.generateDocument(templateSet.id, selectedOfficeSpaceId, placeholderValues, docName, null);
            }

            if (response.success) {
                const templateDocsList = currentTemplateSet?.docs || [];
                const blobEntries = await Promise.all(
                    templateDocsList.map(async (templateDoc) => {
                        if (!templateDoc?.contentKey) return null;
                        try {
                            const blob = await generateDocPdf(
                                templateDoc,
                                currentTemplateSet?.placeholders,
                                applyDocTransforms(liveFormValues, currentTemplateSet?.placeholders, currencyWordsEnabled, landUnitWordsEnabled, currentTemplateSet?.language),
                                { r2Url: R2_URL }
                            );
                            return [templateDoc.id, blob];
                        } catch (err) { console.warn(`Failed to build PDF for doc ${templateDoc.id}:`, err); return null; }
                    })
                );
                setGeneratedBlobs(Object.fromEntries(blobEntries.filter(Boolean)));
                setGeneratedDocs(response.data);

                // Auto-open income/expense modal after successful doc generation
                if (user?.settings?.autoAppearIncomeExpense === true) {
                    const fill = moduleAutoFill(user?.settings, 'doc_generation');
                    openIncomeExpenseModal({
                        name: fill.name,
                        type: 'income',
                        amount: fill.price,
                        category: 'doc_generation',
                    });
                }

                clearFormDraft(currentTemplateSet?.id, documentGroup?.id);
                setHasUnsavedChanges(false);
                setHasDraft(false);
            }
        } catch (error) {
            if (error?.code === 'INSUFFICIENT_CREDITS') { setLimitModalOpen(true); return; }
            notifications.show({ title: t('common.error'), message: error.message || t('documents.failedToGenerate'), color: 'red', autoClose: 8000 });
        } finally { setLoading(false); }
    };

    const handleManualGenerate = async () => {
        setLoading(true);
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        const templateSetId = currentTemplateSet?.id;
        const docName = (liveFormValues.documentName || currentTemplateSet?.name || 'Untitled Document').trim();
        try {
            let generatedSetData;
            if (documentGroup) {
                const response = await generatedDocService.regenerateDoc(documentGroup.id, {}, docName);
                if (!response.success) throw new Error(response.message || t('documents.failedToGenerate'));
                generatedSetData = response.data;
            } else {
                const officeSpaceId = selectedOfficeSpace?.id || null;
                const response = await generatedDocService.generateDocument(templateSetId, officeSpaceId, {}, docName, null);
                if (!response.success) throw new Error(response.message || t('documents.failedToGenerate'));
                generatedSetData = response.data;
            }

            const genDocs = generatedSetData.generatedDocs || [];
            const htmlMap = manualHtmlMapRef.current;
            await Promise.all(
                templateDocs.map(async (templateDoc, i) => {
                    const rawHtml = htmlMap[templateDoc.id] || '';
                    const safeHtml = sanitizeDocHtml(rawHtml);
                    const genDoc = genDocs[i];
                    if (!genDoc) return;
                    await generatedDocService.updateGeneratedDoc(genDoc.id, safeHtml, { isManuallyEdited: true });
                    genDoc.content = safeHtml;
                    genDoc.metadata = { ...(genDoc.metadata || {}), isManuallyEdited: true };
                })
            );

            const blobEntries = await Promise.all(
                templateDocs.map(async (templateDoc) => {
                    const html = htmlMap[templateDoc.id] || '';
                    try {
                        const blob = await generateDocPdf(templateDoc, currentTemplateSet?.placeholders, {}, { rawHtml: html });
                        return [templateDoc.id, blob];
                    } catch (err) { console.warn(`Failed PDF for doc ${templateDoc.id}:`, err); return null; }
                })
            );
            setGeneratedBlobs(Object.fromEntries(blobEntries.filter(Boolean)));
            setGeneratedDocs(generatedSetData);
            clearManualEditData(templateSetId, templateDocs.map(d => d.id));
            saveManualEditMode(templateSetId, false);
            setIsManualEditMode(false);
            setIsEditMode(false);
            setManualHtmlMap({});
            manualHtmlMapRef.current = {};
        } catch (error) {
            if (error?.code === 'INSUFFICIENT_CREDITS') { setLimitModalOpen(true); return; }
            notifications.show({ title: t('common.error'), message: error.message || t('documents.failedToGenerate'), color: 'red', autoClose: 8000 });
        } finally { setLoading(false); }
    };

    const handleFormAutoSave = () => {
        if (!opened || generatedDocs) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            try {
                const formValues = form.getFieldsValue(true);
                const currentValues = JSON.stringify(formValues);
                const hasChanges = initialFormValuesRef.current && currentValues !== initialFormValuesRef.current;
                setHasUnsavedChanges(hasChanges);
                if (formValues.documentName?.trim() && hasChanges) {
                    const currentTemplateSet = templateSet || documentGroup?.templateSet;
                    saveFormDraft(formValues, currentTemplateSet?.id, documentGroup?.id);
                    setHasDraft(true);
                }
            } catch (error) { console.debug('Auto-save error:', error); }
        }, 1000);
    };

    useEffect(() => {
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, []);

    const handleClose = () => {
        if (!generatedDocs) {
            // Save whatever is in the form right now as a draft so nothing is lost
            try {
                const formValues = form.getFieldsValue(true);
                const currentTemplateSet = templateSet || documentGroup?.templateSet;
                if (formValues.documentName?.trim()) {
                    saveFormDraft(formValues, currentTemplateSet?.id, documentGroup?.id);
                    setHasDraft(true);
                }
            } catch (_) {}
        }
        doClose();
    };

    const doClose = () => {
        setHasUnsavedChanges(false);
        initialFormValuesRef.current = null;
        onClose();
    };

    const handleDownload = async (doc) => {
        try {
            if (!doc.fileUrl) { notifications.show({ title: t('common.error'), message: t('documents.documentNotAvailable'), color: 'red' }); return; }
            const cacheBuster = Date.now();
            const downloadUrl = doc.fileUrl.includes('?') ? `${doc.fileUrl}&_=${cacheBuster}` : `${doc.fileUrl}?_=${cacheBuster}`;
            const response = await fetch(downloadUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `${doc.title}.docx`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            notifications.show({ title: t('common.success'), message: t('documents.documentDownloaded'), color: 'green' });
        } catch (error) {
            notifications.show({ title: t('common.error'), message: t('documents.failedToDownload'), color: 'red' });
        }
    };

    const handleDownloadPDF = async (doc) => {
        try {
            if (doc.metadata?.pdfUrl) {
                notifications.show({ title: t('documents.downloadingPdf'), message: t('documents.downloadingDocument'), color: 'blue', autoClose: 2000, id: 'pdf-download' });
                const cacheBuster = Date.now();
                const downloadUrl = doc.metadata.pdfUrl.includes('?') ? `${doc.metadata.pdfUrl}&_=${cacheBuster}` : `${doc.metadata.pdfUrl}?_=${cacheBuster}`;
                const response = await fetch(downloadUrl);
                if (!response.ok) throw new Error('Failed to fetch PDF');
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `${doc.title}.pdf`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                notifications.hide('pdf-download');
                notifications.show({ title: t('common.success'), message: t('documents.pdfDownloaded'), color: 'green' });
                return;
            }
            if (!doc.fileUrl) { notifications.show({ title: t('common.error'), message: t('documents.documentNotAvailable'), color: 'red' }); return; }
            notifications.show({ title: t('documents.convertingToPDF'), message: t('documents.pleaseWaitConverting'), color: 'blue', autoClose: false, id: 'pdf-conversion' });
            const blob = await generatedDocService.convertToPDF(doc.fileUrl, doc.title);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `${doc.title}.pdf`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            notifications.hide('pdf-conversion');
            notifications.show({ title: t('common.success'), message: t('documents.pdfDownloaded'), color: 'green' });
        } catch (error) {
            notifications.hide('pdf-conversion'); notifications.hide('pdf-download');
            notifications.show({ title: t('common.error'), message: t('documents.failedToDownloadPDF') || error.message, color: 'red' });
        }
    };

    const resolveBlob = async (templateDoc) => {
        if (generatedBlobs[templateDoc?.id]) return generatedBlobs[templateDoc.id];
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        const blob = await generateDocPdf(
            templateDoc, currentTemplateSet?.placeholders,
            applyDocTransforms(liveFormValues, currentTemplateSet?.placeholders, currencyWordsEnabled, landUnitWordsEnabled, currentTemplateSet?.language),
            { r2Url: R2_URL }
        );
        setGeneratedBlobs(prev => ({ ...prev, [templateDoc.id]: blob }));
        return blob;
    };

    const handlePrint = async (templateDoc, title) => {
        const notifId = 'pdf-print';
        try {
            notifications.show({ id: notifId, title: 'PDF তৈরি...', color: 'blue', loading: true, autoClose: false });
            const blob = await resolveBlob(templateDoc);
            notifications.hide(notifId);
            const opened = await printBlob(blob);
            if (!opened) notifications.show({ title: t('common.error'), message: t('documents.allowPopupsForPrint'), color: 'red' });
        } catch (err) {
            notifications.update({ id: notifId, title: t('common.error'), message: err.message, color: 'red', loading: false, autoClose: 3000 });
        }
    };

    const handleDownloadGenerated = async (templateDoc, title) => {
        const notifId = 'pdf-dl';
        try {
            notifications.show({ id: notifId, title: 'PDF তৈরি...', color: 'blue', loading: true, autoClose: false });
            const blob = await resolveBlob(templateDoc);
            notifications.hide(notifId);
            downloadBlob(blob, `${title}.pdf`);
        } catch (err) {
            notifications.update({ id: notifId, title: t('common.error'), message: err.message, color: 'red', loading: false, autoClose: 3000 });
        }
    };

    // The HTML of one generated document, exactly as its PDF was drawn (or the saved manual edit).
    const buildDocHtml = async (templateDoc, index) => {
        if (isGeneratedManualEdit) {
            const saved = generatedDocs?.generatedDocs?.[index]?.content;
            if (saved) return saved;
        }
        if (!templateDoc?.contentKey) return '';
        const res = await fetch(`${R2_URL}/${templateDoc.contentKey}?t=${Date.now()}`);
        if (!res.ok) throw new Error(t('documents.failedToGenerate'));
        const json = await res.json();
        return replacePlaceholders(lexicalToHtml(json), documentValues, placeholdersConfig);
    };

    const openDocEditor = async (templateDoc, index, fileName) => {
        if (docEditorBusyId) return;
        setDocEditorBusyId(templateDoc.id);
        try {
            const { pageSize, margin } = resolvePageSettings(templateDoc);
            const pageMargins = { top: margin.top * 0.75, bottom: margin.bottom * 0.75 };
            const saved = docEditorLayoutsRef.current[templateDoc.id];
            const built = saved
                ? { ...(await objectLayoutToPdf(saved, fileName)), pageMargins }
                : await manualHtmlToDoc({
                    html: await buildDocHtml(templateDoc, index),
                    name: fileName,
                    page: pageSize,
                    margins: margin,
                    stamp: templateDoc?.metadata?.stamp === true,
                });
            setDocEditor({ doc: templateDoc, file: built.file, objects: built.objects, pageMargins: built.pageMargins || pageMargins });
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('documents.failedToGenerate'), color: 'red' });
        } finally { setDocEditorBusyId(null); }
    };

    // Closing never waits: snapshot the edit (the editor's store resets as it unmounts), then leave.
    const closeDocEditor = () => {
        const current = docEditor;
        if (current) {
            try {
                const st = usePdfEditorStore.getState();
                const layout = serializeEditorLayout(st.pages, st.objectsByUid);
                if (layout.objects.length) docEditorLayoutsRef.current[current.doc.id] = layout;
            } catch { /* keep the previous snapshot */ }
        }
        setDocEditor(null);
    };

    const handleShareGenerated = async () => {
        const currentTemplateSet = templateSet || documentGroup?.templateSet;
        const docName = form.getFieldValue('documentName') || 'document';
        const notifId = 'pdf-share';
        try {
            notifications.show({ id: notifId, title: 'শেয়ারের জন্য তৈরি হচ্ছে...', color: 'blue', loading: true, autoClose: false });
            const files = await Promise.all(
                (currentTemplateSet?.docs || []).map(async (doc) => {
                    const blob = await resolveBlob(doc);
                    return new File([blob], `${docName}${doc.title ? ' - ' + doc.title : ''}.pdf`, { type: 'application/pdf' });
                })
            );
            notifications.hide(notifId);
            const used = await shareBlobs(files, docName);
            if (!used) notifications.show({ message: 'PDF ডাউনলোড হচ্ছে।', color: 'blue', autoClose: 2000 });
        } catch (err) {
            notifications.hide(notifId);
            if (err.name !== 'AbortError') notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        }
    };

    const handleDone = () => {
        if (onSuccess) onSuccess(generatedDocs);
        onClose();
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!opened || docEditor) return; // the editor has its own shortcuts
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyB' && !unicodeModalOpened) {
                e.preventDefault(); setUnicodeModalOpened(true); return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                if (generatedDocs) handleDone();
                else if (isOnLastStep) handleGenerate();
                return;
            }
            if (generatedDocs && (e.ctrlKey || e.metaKey)) {
                const key = e.key.toLowerCase();
                const firstDoc = generatedDocs.generatedDocs?.[0];
                if (firstDoc) {
                    if (key === 'p') { e.preventDefault(); handlePrint(firstDoc); return; }
                    if (key === 'd') { e.preventDefault(); handleDownload(firstDoc); return; }
                    if (key === 'e') { e.preventDefault(); handleDownloadPDF(firstDoc); return; }
                }
            }
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'ArrowRight') { e.preventDefault(); handleNext(); }
            else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'ArrowLeft') { e.preventDefault(); handleBack(); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [opened, generatedDocs, isOnLastStep, unicodeModalOpened, docEditor]);

    const currentTemplateSet = templateSet || documentGroup?.templateSet;
    const docLanguage = currentTemplateSet?.language || 'bangla';
    const previewAvailable = !generatedDocs && !!(currentTemplateSet?.docs?.some(d => d.contentKey || d.fileUrl || d.body));
    const allTemplateDocs = currentTemplateSet?.docs || [];
    const templateDocs = allTemplateDocs.filter(d => !excludedDocIds.has(d.id));
    const isStampDoc = templateDocs[activeDocIndex]?.metadata?.stamp === true;
    const stampGloballyEnabled = user?.settings?.stampPreviewEnabled !== false;
    const isGeneratedManualEdit = !!generatedDocs?.generatedDocs?.some(d => d?.metadata?.isManuallyEdited === true);
    const previewShowing = previewOpen || isStampDoc;

    const placeholdersConfig = useMemo(() => {
        if (!currentTemplateSet?.placeholders) return {};
        const config = {};
        currentTemplateSet.placeholders.forEach(p => {
            config[p.slug] = { placeholder: p.placeholder, title: p.title, type: p.type };
        });
        return config;
    }, [currentTemplateSet?.placeholders]);

    const documentValues = useMemo(
        () => applyDocTransforms(liveFormValues, currentTemplateSet?.placeholders, currencyWordsEnabled, landUnitWordsEnabled, currentTemplateSet?.language),
        [liveFormValues, currentTemplateSet?.placeholders, currencyWordsEnabled, landUnitWordsEnabled, currentTemplateSet?.language]
    );

    const detectTypeFromPattern = (pattern) => {
        const trimmed = (pattern || '').trim();
        if (trimmed === '==' || trimmed.startsWith('==')) return 'Steps';
        if (trimmed === '--' || trimmed.startsWith('--')) return 'Separator';
        if (trimmed === '##' || trimmed.startsWith('##')) return 'Insert';
        if (trimmed === '++' || trimmed.startsWith('++')) return 'Increment';
        if (trimmed === '**' || trimmed.startsWith('**')) return 'BulletList';
        if (trimmed === '@@V' || trimmed.startsWith('@@V')) return 'TableBuilder';
        if (trimmed === '@@' || trimmed.startsWith('@@')) return 'TableBuilder';
        return 'Placeholder';
    };

    useEffect(() => {
        if (!currentTemplateSet?.placeholders) { setDynamicSteps([]); return; }
        const placeholders = currentTemplateSet.placeholders
            .map(p => ({ ...p, type: p.type && p.type !== 'Placeholder' ? p.type : detectTypeFromPattern(p.pattern) }))
            .sort((a, b) => a.order - b.order);
        const steps = [];
        let currentStepPlaceholders = [];
        let currentStepInfo = null;
        placeholders.forEach((placeholder) => {
            if (placeholder.type === 'Steps') {
                if (currentStepPlaceholders.length > 0 || currentStepInfo) {
                    steps.push({ label: currentStepInfo?.title || t('documents.fillInformation'), description: currentStepInfo?.description || '', placeholders: currentStepPlaceholders });
                }
                currentStepInfo = placeholder;
                currentStepPlaceholders = [];
            } else {
                currentStepPlaceholders.push(placeholder);
            }
        });
        if (currentStepPlaceholders.length > 0 || currentStepInfo) {
            steps.push({ label: currentStepInfo?.title || t('documents.fillInformation'), description: currentStepInfo?.description || '', placeholders: currentStepPlaceholders });
        }
        if (steps.length === 0) {
            const nonStep = placeholders.filter(p => p.type !== 'Steps');
            if (nonStep.length > 0) steps.push({ label: t('documents.fillInformation'), description: t('documents.completeAllFields'), placeholders: nonStep });
        }
        setDynamicSteps(steps);
    }, [currentTemplateSet, t]);

    const isFormComplete = useMemo(() => {
        const values = liveFormValues || {};
        if (!String(values.documentName || '').trim()) return false;
        const placeholders = (currentTemplateSet?.placeholders || []).map(p => ({
            ...p, type: p.type && p.type !== 'Placeholder' ? p.type : detectTypeFromPattern(p.pattern)
        }));
        return placeholders.every((placeholder) => {
            if (placeholder.type === 'Steps' || placeholder.type === 'Separator') return true;
            const value = values[placeholder.slug];
            if (placeholder.type === 'TableBuilder') return value && Array.isArray(value.headers) && value.headers.length > 0 && Array.isArray(value.rows) && value.rows.length > 0 && value.rows.some(row => Array.isArray(row) && row.some(cell => String(cell || '').trim()));
            if (placeholder.type === 'Increment' || placeholder.type === 'BulletList') return value && Array.isArray(value.items) && value.items.some(item => String(item || '').trim());
            if (placeholder.type === 'Insert') { if (!values[`${placeholder.slug}_enabled`]) return true; return value && String(value).trim(); }
            return value && String(value).trim();
        });
    }, [currentTemplateSet, liveFormValues]);

    // ── Render a placeholder field ──────────────────────────────────────────────
    const renderPlaceholderField = (placeholder) => {
        if (placeholder.type === 'Steps') return null;

        if (placeholder.type === 'Separator') {
            return (
                <div key={placeholder.id} className="mt-5 mb-3">
                    <p className="text-sm font-semibold text-center text-gray-700 dark:text-gray-200 mb-2">{placeholder.title}</p>
                    <hr className="border-gray-300 dark:border-gray-600" />
                    {placeholder.description && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1.5">{placeholder.description}</p>
                    )}
                </div>
            );
        }

        const placeholderText = (placeholder.placeholder || '').replace(/\{\{|\}\}/g, '');
        const labelText = placeholder.title;
        const fieldDescription = placeholder.type === 'Insert' ? null : placeholder.description;

        return (
            <div key={placeholder.id} data-slug={placeholder.slug}>
                {placeholder.type === 'TableBuilder' ? (
                    <Field name={placeholder.slug}>
                        {(control) => (
                            <TableBuilderInput
                                label={labelText}
                                description={placeholder.description}
                                orientation={placeholder.orientation || 'horizontal'}
                                value={control.value || { headers: [], rows: [], orientation: placeholder.orientation || 'horizontal' }}
                                onChange={(value) => { control.onChange(value); handleFormAutoSave(); }}
                                onFocus={() => setActivePlaceholderSlug(placeholder.slug)}
                            />
                        )}
                    </Field>
                ) : (placeholder.type === 'Increment' || placeholder.type === 'BulletList') ? (
                    <Field name={placeholder.slug}>
                        {(control) => (
                            <ListBuilderInput
                                label={labelText}
                                description={placeholder.description}
                                listType={placeholder.type}
                                language={currentTemplateSet?.language || 'bangla'}
                                value={control.value || { items: [], listType: placeholder.type }}
                                onChange={(value) => { control.onChange(value); handleFormAutoSave(); }}
                                onFocus={() => setActivePlaceholderSlug(placeholder.slug)}
                            />
                        )}
                    </Field>
                ) : placeholder.type === 'Insert' ? (
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{labelText}</span>
                            <Field name={`${placeholder.slug}_enabled`}>
                                {(toggleControl) => (
                                    <Switch
                                        size="sm"
                                        checked={toggleControl.value || false}
                                        onChange={(event) => { toggleControl.onChange(event.currentTarget.checked); handleFormAutoSave(); }}
                                    />
                                )}
                            </Field>
                        </div>
                        <Field name={placeholder.slug}>
                            {(control, meta) => (
                                <Textarea
                                    value={control.value || ''}
                                    onChange={(e) => { control.onChange(e.target.value); handleFormAutoSave(); }}
                                    onFocus={() => setActivePlaceholderSlug(placeholder.slug)}
                                    placeholder={placeholderText || `Enter ${placeholder.title}`}
                                    minRows={1}
                                    error={meta.errors?.[0]}
                                />
                            )}
                        </Field>
                    </div>
                ) : (
                    <Field name={placeholder.slug}>
                        {(control, meta) => {
                            let dropdownOptions = [];
                            if (placeholder.options) {
                                try {
                                    dropdownOptions = placeholder.options.startsWith('[')
                                        ? JSON.parse(placeholder.options)
                                        : placeholder.options.split(',').map(opt => opt.trim());
                                } catch (e) { console.warn('Failed to parse dropdown options:', e); }
                            }
                            if (PREDEFINED_TYPES.has(placeholder.type)) dropdownOptions = PREDEFINED_TYPE_OPTIONS[placeholder.type];

                            const fieldType = placeholder.type;
                            const placeholderHint = placeholderText || `Enter ${placeholder.title}`;

                            // Label with optional badge for Placeholder type
                            const CustomLabel = placeholder.type === 'Placeholder' ? (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{labelText}</span>
                                    {placeholder.pattern?.includes('[[') && placeholder.pattern?.includes(']]') && (() => {
                                        const match = placeholder.pattern.match(/^(.*?)\[\[(.*?)\]\](.*)$/);
                                        if (match) {
                                            const [, prefix, , postfix] = match;
                                            return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{prefix}VALUE{postfix}</span>;
                                        }
                                        return null;
                                    })()}
                                </div>
                            ) : labelText;

                            const commonLabelProps = {
                                label: CustomLabel,
                                description: fieldDescription,
                                error: meta.errors?.[0],
                            };

                            const onFocusPh = () => setActivePlaceholderSlug(placeholder.slug);

                            // Dropdown (admin options, non-special type)
                            if (dropdownOptions.length > 0 && !['YesNo','Date','DateBar','CustomToggle','Options','LandUnit','Currency'].includes(fieldType) && !PREDEFINED_TYPES.has(fieldType)) {
                                return (
                                    <div>
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <SelectField
                                            value={control.value || ''}
                                            onChange={(v) => { onFocusPh(); control.onChange(v); handleFormAutoSave(); }}
                                            options={dropdownOptions.map(opt => ({ value: opt, label: opt }))}
                                            placeholder={placeholderText || `Select ${placeholder.title}`}
                                            triggerClassName={inputClass}
                                            menuZIndex={2147483647}
                                        />
                                    </div>
                                );
                            }

                            if (fieldType === 'Currency') {
                                const wordsOn = currencyWordsEnabled[placeholder.slug] !== false;
                                const rawVal = control.value || '';
                                const bnWords = wordsOn && rawVal ? numberToBengaliWords(rawVal) : '';
                                return (
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                            <Switch size="xs" label="কথায় দেখাও" checked={wordsOn}
                                                onChange={(e) => setCurrencyWordsEnabled(prev => ({ ...prev, [placeholder.slug]: e.currentTarget.checked }))} />
                                        </div>
                                        <input className={inputClass} value={rawVal} inputMode="decimal" onFocus={onFocusPh} placeholder={placeholderHint}
                                            onChange={(e) => { control.onChange(sanitizeBnEnNumber(e.target.value)); handleFormAutoSave(); }} />
                                        {wordsOn && bnWords && <span className="text-xs text-primary dark:text-primary-300 italic pl-0.5">{bnWords}</span>}
                                    </div>
                                );
                            }

                            if (fieldType === 'Options' || PREDEFINED_TYPES.has(fieldType)) {
                                return (
                                    <div className="flex flex-col gap-1.5">
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <input className={inputClass} value={control.value || ''} placeholder={placeholderHint} onFocus={onFocusPh}
                                            onChange={(e) => { control.onChange(e.target.value); handleFormAutoSave(); }} />
                                        {dropdownOptions.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {dropdownOptions.map((opt) => (
                                                    <Badge key={opt} active={control.value === opt} onClick={() => { control.onChange(opt); handleFormAutoSave(); }}>{opt}</Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (fieldType === 'LandUnit') {
                                const fullValue = control.value || '';
                                const lastSpaceIdx = fullValue.lastIndexOf(' ');
                                let parsedAmount = fullValue, parsedUnit = '';
                                if (lastSpaceIdx !== -1) {
                                    const tail = fullValue.slice(lastSpaceIdx + 1);
                                    if (LAND_UNITS.includes(tail)) { parsedAmount = fullValue.slice(0, lastSpaceIdx); parsedUnit = tail; }
                                }
                                const handleAmountChange = (newAmount) => {
                                    control.onChange(parsedUnit ? `${newAmount} ${parsedUnit}` : newAmount);
                                    handleFormAutoSave();
                                };
                                const handleUnitSelect = (unit) => {
                                    const newUnit = parsedUnit === unit ? '' : unit;
                                    control.onChange(newUnit ? `${parsedAmount} ${newUnit}` : parsedAmount);
                                    handleFormAutoSave();
                                };
                                const wordsOn = landUnitWordsEnabled[placeholder.slug] !== false;
                                const bnWords = wordsOn && parsedAmount ? numberToBengaliWords(parsedAmount) : '';
                                return (
                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex items-center justify-between gap-2">
                                            <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                            <Switch size="xs" label="কথায় দেখাও" checked={wordsOn}
                                                onChange={(e) => setLandUnitWordsEnabled(prev => ({ ...prev, [placeholder.slug]: e.currentTarget.checked }))} />
                                        </div>
                                        <input className={inputClass} value={parsedAmount} inputMode="decimal" onFocus={onFocusPh} placeholder={placeholderHint}
                                            onChange={(e) => handleAmountChange(e.target.value)} />
                                        {bnWords && <span className="text-xs text-primary dark:text-primary-300 italic pl-0.5">{bnWords}</span>}
                                        <div className="flex flex-wrap gap-1.5">
                                            {LAND_UNITS.map((unit) => (
                                                <Badge key={unit} active={parsedUnit === unit} onClick={() => handleUnitSelect(unit)}>{unit}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }

                            if (fieldType === 'CustomToggle') {
                                return (
                                    <div>
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <div className="flex flex-wrap gap-3 mt-1.5">
                                            {dropdownOptions.length === 0
                                                ? <span className="text-xs text-gray-400">No options configured for this toggle.</span>
                                                : dropdownOptions.map((opt) => (
                                                    <Radio key={opt} value={opt} label={opt} currentValue={control.value || ''} onChange={(v) => { control.onChange(v); handleFormAutoSave(); }} />
                                                ))
                                            }
                                        </div>
                                    </div>
                                );
                            }

                            if (fieldType === 'YesNo') {
                                return (
                                    <div>
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <div className="flex gap-4 mt-1.5">
                                            <Radio value="হ্যাঁ" label="হ্যাঁ" currentValue={control.value || ''} onChange={(v) => { control.onChange(v); handleFormAutoSave(); }} />
                                            <Radio value="না" label="না" currentValue={control.value || ''} onChange={(v) => { control.onChange(v); handleFormAutoSave(); }} />
                                        </div>
                                    </div>
                                );
                            }

                            if (fieldType === 'Date') {
                                const dateValue = parseBnDateString(control.value);
                                return (
                                    <div>
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <DatePickerField
                                            value={dateValue}
                                            triggerClassName={inputClass}
                                            fromYear={1990}
                                            toYear={2100}
                                            contentClassName="z-[2147483647]"
                                            onChange={(d) => {
                                                onFocusPh();
                                                control.onChange(d ? formatDocDate(d, docLanguage) : '');
                                                handleFormAutoSave();
                                            }}
                                        />
                                    </div>
                                );
                            }

                            if (fieldType === 'DateBar') {
                                const dateValue = parseBnDateString(control.value);
                                const isEnDoc = isEnglishDoc(docLanguage);
                                const dayName = dateValue ? (isEnDoc ? EN_DAYS : BN_DAYS)[dateValue.getDay()] : null;
                                return (
                                    <div className="flex flex-col gap-1">
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <DatePickerField
                                            value={dateValue}
                                            triggerClassName={inputClass}
                                            fromYear={1990}
                                            toYear={2100}
                                            contentClassName="z-[2147483647]"
                                            onChange={(d) => {
                                                onFocusPh();
                                                control.onChange(d ? formatDocDate(d, docLanguage) : '');
                                                handleFormAutoSave();
                                            }}
                                        />
                                        {control.value && dayName && (
                                            <span className="text-xs text-gray-400 dark:text-gray-500 pl-0.5">{control.value}{isEnDoc ? ',' : ' ইং তারিখ'} {dayName}</span>
                                        )}
                                    </div>
                                );
                            }

                            if (fieldType === 'Textarea') {
                                return (
                                    <div>
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <textarea className={`${inputClass} resize-none`} rows={2} value={control.value || ''} placeholder={placeholderHint} onFocus={onFocusPh}
                                            onChange={(e) => { control.onChange(e.target.value); handleFormAutoSave(); }} />
                                    </div>
                                );
                            }

                            if (fieldType === 'Number') {
                                return (
                                    <div>
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <input className={inputClass} value={control.value || ''} inputMode="decimal" placeholder={placeholderHint} onFocus={onFocusPh}
                                            onChange={(e) => { control.onChange(sanitizeBnEnNumber(e.target.value)); handleFormAutoSave(); }} />
                                    </div>
                                );
                            }

                            if (fieldType === 'Phone') {
                                return (
                                    <div>
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <input type="tel" className={inputClass} value={control.value || ''} inputMode="tel" placeholder={placeholderHint} onFocus={onFocusPh}
                                            onChange={(e) => { control.onChange(sanitizeBnEnNumber(e.target.value)); handleFormAutoSave(); }} />
                                    </div>
                                );
                            }

                            if (fieldType === 'Email') {
                                return (
                                    <div>
                                        <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                        <input type="email" className={inputClass} value={control.value || ''} inputMode="email" placeholder={placeholderHint} onFocus={onFocusPh}
                                            onChange={(e) => { control.onChange(e.target.value); handleFormAutoSave(); }} />
                                    </div>
                                );
                            }

                            // Default: text input
                            return (
                                <div>
                                    <FieldLabel description={fieldDescription} error={meta.errors?.[0]}>{CustomLabel}</FieldLabel>
                                    <input className={inputClass} value={control.value || ''} placeholder={placeholderHint} onFocus={onFocusPh}
                                        onChange={(e) => { control.onChange(e.target.value); handleFormAutoSave(); }} />
                                </div>
                            );
                        }}
                    </Field>
                )}
            </div>
        );
    };

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <>
            <Sheet open={opened && !docEditor} onOpenChange={(open) => { if (!open) handleClose(); }}>
                <SheetContent
                    side="right"
                    showClose={false}
                    className="p-0 gap-0 flex overflow-hidden w-full max-w-full sm:max-w-full"
                    style={{
                        width: isMobile ? '100%' : (previewShowing ? '100%' : '40%'),
                        flexDirection: (!isMobile && previewShowing) ? 'row' : 'column',
                        zIndex: 2147483647,
                        maxWidth: '100vw',
                        fontFamily: "'Hind Siliguri', 'Noto Sans Bengali', sans-serif",
                    }}
                    onInteractOutside={(e) => e.preventDefault()}
                    aria-describedby={undefined}
                >
                    <SheetTitle className="sr-only">Document Generator</SheetTitle>
                    {/* Loading overlay */}
                    {loading && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm">
                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {/* Manual Edit floating UI */}
                    {isManualEditMode && !generatedDocs && (
                        <>
                            {/* Close button */}
                            <TipBtn
                                onClick={handleClose}
                                tip={t('common.close')}
                                side="left"
                                className="absolute top-3 right-4 z-[110] w-8 h-8 flex items-center justify-center rounded-full bg-red-600 text-white shadow-lg hover:bg-red-700 transition-colors"
                            >
                                <IconX size={18} />
                            </TipBtn>

                            {/* Doc tabs — only when multiple docs */}
                            {templateDocs.length > 0 && allTemplateDocs.length > 1 && (
                                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[100] flex gap-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-1.5 py-1 shadow-md">
                                    {templateDocs.map((doc, i) => (
                                        <div key={doc.id} className="flex items-center gap-0.5">
                                            <Btn size="xs" variant={activeDocIndex === i ? 'primary' : 'default'} onClick={() => setActiveDocIndex(i)}>
                                                {doc.title || `Doc ${i + 1}`}
                                            </Btn>
                                            {templateDocs.length > 1 && (
                                                <button
                                                    type="button"
                                                    title="Remove this page"
                                                    onClick={() => {
                                                        setExcludedDocIds(prev => new Set([...prev, doc.id]));
                                                        if (activeDocIndex >= i && activeDocIndex > 0) setActiveDocIndex(activeDocIndex - 1);
                                                    }}
                                                    className="w-4 h-4 flex items-center justify-center rounded-full text-zinc-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30 transition-colors"
                                                >
                                                    <IconX size={10} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Floating action bar */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[25] flex flex-row gap-1.5 items-center bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-700 rounded-xl px-2 py-1.5 shadow-xl backdrop-blur-sm">
                                <TooltipButton hotkeyAction="GENERATE_DOCUMENT" onClick={handleManualGenerate} loading={loading}>
                                    {isRegenerating ? t('documents.regenerateDocs') : t('documents.generateDocuments')}
                                </TooltipButton>
                                <Btn
                                    size="xs"
                                    variant={showPlaceholderHighlights ? 'yellowFilled' : 'default'}
                                    leftIcon={showPlaceholderHighlights ? <IconHighlight size={14} /> : <IconHighlightOff size={14} />}
                                    onClick={() => setShowPlaceholderHighlights(v => !v)}
                                    title={showPlaceholderHighlights ? t('documents.hideHighlights') : t('documents.showHighlights')}
                                >
                                    {t('documents.manualEditBarLabel_highlights')}
                                </Btn>
                                {isStampDoc && (
                                    <>
                                        <Btn
                                            size="xs"
                                            variant={stampVisible ? 'greenFilled' : 'default'}
                                            leftIcon={<IconAward size={14} />}
                                            onClick={async () => {
                                                const next = !stampVisible;
                                                setStampVisible(next);
                                                try { await updateSettings({ stampVisible: next }); } catch (_) {}
                                            }}
                                        >
                                            {t('documents.manualEditBarLabel_stamp')}
                                        </Btn>
                                        {stampVisible && (
                                            <select
                                                value={String(stampDenomination)}
                                                onChange={(e) => setStampDenomination(Number(e.target.value))}
                                                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 w-[70px]"
                                            >
                                                <option value="50">৳৫০</option>
                                                <option value="100">৳১০০</option>
                                            </select>
                                        )}
                                    </>
                                )}
                                <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-0.5" />
                                <Btn size="xs" variant="redSubtle" leftIcon={<IconBan size={14} />} onClick={() => setDestroyManualConfirmOpen(true)}
                                    title={t('documents.destroyManualEditTitle')}>
                                    {t('documents.manualEditBarLabel_reset')}
                                </Btn>
                            </div>
                        </>
                    )}

                    {/* Mobile panel toggle — hidden in manual edit mode */}
                    {isMobile && previewShowing && !isManualEditMode && (
                        <div className="flex shrink-0 border-b border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setMobilePanelView('form')}
                                className={`flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5 font-medium border-b-2 transition-colors ${
                                    mobilePanelView === 'form'
                                        ? 'bg-orange-50 dark:bg-orange-900/10 text-orange-700 dark:text-orange-400 border-orange-500'
                                        : 'bg-white dark:bg-gray-900 text-gray-400 dark:text-gray-500 border-transparent'
                                }`}
                            >
                                <IconForms size={15} /> ফর্ম
                            </button>
                            <button
                                onClick={() => setMobilePanelView('preview')}
                                className={`flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5 font-medium border-b-2 transition-colors ${
                                    mobilePanelView === 'preview'
                                        ? 'bg-orange-50 dark:bg-orange-900/10 text-orange-700 dark:text-orange-400 border-orange-500'
                                        : 'bg-white dark:bg-gray-900 text-gray-400 dark:text-gray-500 border-transparent'
                                }`}
                            >
                                <IconEye size={15} /> প্রিভিউ
                            </button>
                        </div>
                    )}

                    {/* Preview panel */}
                    {previewShowing && (isMobile ? (isManualEditMode || mobilePanelView === 'preview') : true) && (
                        <div
                            style={{
                                flex: (isMobile || isManualEditMode) ? '1 1 auto' : (isNarrowDesktop ? '1 1 calc(75% - 100px)' : '0 0 75%'),
                                width: (isMobile || isManualEditMode) ? '100%' : (isNarrowDesktop ? 'calc(75% - 100px)' : '75%'),
                                height: (isMobile && !isManualEditMode) ? 'calc(100% - 44px)' : '100%',
                                display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
                                borderRight: (!isMobile && !isManualEditMode) ? '1px solid var(--border)' : 'none',
                            }}
                        >
                            <DocPreviewPanel
                                templateDoc={templateDocs[activeDocIndex]}
                                formValues={documentValues}
                                activeSlug={activePlaceholderSlug}
                                pageRef={previewPageRef}
                                isExporting={isExportingPdf}
                                placeholders={placeholdersConfig}
                                showStamp={isStampDoc ? (stampGloballyEnabled && stampVisible) : undefined}
                                stampDenomination={stampDenomination}
                                isEditMode={isEditMode}
                                onContentChange={isManualEditMode ? (html) => {
                                    const docId = templateDocs[activeDocIndex]?.id;
                                    if (docId) handleManualContentChange(docId, html);
                                } : null}
                                initialHtml={isManualEditMode ? (manualHtmlMap[templateDocs[activeDocIndex]?.id] ?? null) : null}
                                hideHighlights={isManualEditMode && !showPlaceholderHighlights}
                            />
                        </div>
                    )}

                    {/* Right side: form + footer — hidden in manual edit mode */}
                    <div
                        style={{
                            flex: (!isMobile && previewShowing && !isManualEditMode) ? (isNarrowDesktop ? '0 0 calc(25% + 100px)' : '0 0 25%') : '1 1 auto',
                            width: (!isMobile && previewShowing && !isManualEditMode) ? (isNarrowDesktop ? 'calc(25% + 100px)' : '25%') : '100%',
                            display: isManualEditMode ? 'none' : (isMobile && previewShowing && mobilePanelView === 'preview') ? 'none' : 'flex',
                            flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden',
                        }}
                    >
                        {/* Header */}
                        <div className="shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 py-3 bg-white dark:bg-gray-900">
                            <div className={`flex items-center justify-between ${templateDocs.length > 1 ? 'mb-1.5' : ''}`}>
                                {isEditingName ? (
                                    <input
                                        ref={documentNameRef}
                                        value={editingNameValue}
                                        autoFocus
                                        className={`${inputClass} flex-1 mr-1 text-xs`}
                                        onChange={(e) => setEditingNameValue(e.target.value)}
                                        onBlur={() => {
                                            const trimmed = editingNameValue.trim() || form.getFieldValue('documentName') || 'Untitled Document';
                                            form.setFieldValue('documentName', trimmed);
                                            setEditingNameValue(trimmed);
                                            setLiveFormValues(prev => ({ ...prev, documentName: trimmed }));
                                            setIsEditingName(false);
                                            handleFormAutoSave();
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') e.currentTarget.blur();
                                            if (e.key === 'Escape') { setEditingNameValue(form.getFieldValue('documentName') || 'Untitled Document'); setIsEditingName(false); }
                                        }}
                                    />
                                ) : (
                                    <span
                                        className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex-1 leading-tight cursor-default"
                                        title="Double-click to rename"
                                        onDoubleClick={() => {
                                            const current = form.getFieldValue('documentName') || 'Untitled Document';
                                            setEditingNameValue(current);
                                            setIsEditingName(true);
                                        }}
                                    >
                                        {liveFormValues?.documentName || (isRegenerating ? documentGroup?.title : currentTemplateSet?.name) || t('documents.generateDocuments')}
                                    </span>
                                )}
                                <button
                                    onClick={handleClose}
                                    className="ml-2 w-8 h-8 flex items-center justify-center rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors shrink-0"
                                >
                                    <IconX size={16} />
                                </button>
                            </div>

                            {/* Doc tabs */}
                            {templateDocs.length > 1 && (
                                <div className="flex flex-wrap gap-1">
                                    {templateDocs.map((doc, i) => {
                                        const title = doc.title || `Document ${i + 1}`;
                                        const truncatedTitle = title.length > 20 ? `${title.substring(0, 20)}…` : title;
                                        const isActive = activeDocIndex === i;
                                        return (
                                            <TipBtn
                                                key={doc.id || i}
                                                onClick={() => setActiveDocIndex(i)}
                                                tip={title.length > 20 ? title : undefined}
                                                className={`px-2 py-0.5 rounded text-[11px] border transition-all whitespace-nowrap leading-5 ${
                                                    isActive
                                                        ? 'border-primary bg-primary text-white font-semibold'
                                                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                }`}
                                            >
                                                {truncatedTitle}
                                            </TipBtn>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Scrollable content */}
                        <div
                            className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 min-h-0"
                            style={isEditMode ? { pointerEvents: 'none', opacity: 0.45, userSelect: 'none' } : {}}
                        >
                            {/* Alerts */}
                            {opened && !currentTemplateSet && (
                                <Alert icon={<IconAlertCircle size={16} />} color="red" title={t('documents.missingTemplateSetData')} className="mb-3">
                                    {t('documents.missingTemplateSetMessage')}
                                </Alert>
                            )}
                            {opened && isRegenerating && currentTemplateSet && !currentTemplateSet.placeholders && (
                                <Alert icon={<IconAlertCircle size={16} />} color="orange" title={t('documents.missingPlaceholderData')} className="mb-3">
                                    {t('documents.missingPlaceholderMessage')}
                                </Alert>
                            )}

                            <Form
                                form={form}
                                onValuesChange={(_, allValues) => setLiveFormValues(prev => ({ ...prev, ...allValues }))}
                            >
                                {!generatedDocs ? (
                                    <>
                                        {dynamicSteps.length > 0 && (
                                            <>
                                                <StepIndicator steps={dynamicSteps} activeStep={activeStep} />

                                                {dynamicSteps.map((step, stepIndex) => (
                                                    <div
                                                        key={stepIndex}
                                                        data-step-index={stepIndex}
                                                        className={stepIndex === activeStep ? 'block' : 'hidden'}
                                                    >
                                                        <div
                                                            className="flex flex-col gap-4 mt-3"
                                                            onFocus={(e) => {
                                                                const slugEl = e.target.closest('[data-slug]');
                                                                if (slugEl) setActivePlaceholderSlug(slugEl.dataset.slug);
                                                            }}
                                                        >
                                                            {step.placeholders.map((placeholder) => renderPlaceholderField(placeholder))}

                                                            {/* Step nav buttons */}
                                                            {activeStep < dynamicSteps.length - 1 ? (
                                                                <div className="flex items-center justify-between mt-2">
                                                                    {activeStep > 0 ? (
                                                                        <Btn variant="default" size="sm" leftIcon={<span>←</span>} onClick={handleBack}>
                                                                            {t('common.back')}
                                                                        </Btn>
                                                                    ) : <span />}
                                                                    <Btn variant="primary" size="sm" rightIcon={<span>→</span>} onClick={handleNext} className="ml-auto">
                                                                        {t('common.next')}
                                                                    </Btn>
                                                                </div>
                                                            ) : (
                                                                <div className="mt-2 border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden">
                                                                    {/* Row 1: Back + Manually Edit */}
                                                                    <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-zinc-50 dark:bg-zinc-800/60 border-b border-zinc-200 dark:border-zinc-700">
                                                                        {activeStep > 0 && (
                                                                            <button
                                                                                onClick={handleBack}
                                                                                className="flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 border border-zinc-200 dark:border-zinc-700 rounded-lg hover:bg-white dark:hover:bg-zinc-700 transition-colors shrink-0"
                                                                            >
                                                                                <span>←</span> {t('common.back')}
                                                                            </button>
                                                                        )}
                                                                        {previewAvailable && (
                                                                            <button
                                                                                onClick={() => setManualEditConfirmOpen(true)}
                                                                                className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg hover:bg-white dark:hover:bg-zinc-700 transition-colors whitespace-nowrap"
                                                                            >
                                                                                <IconPencil size={14} />
                                                                                {t('documents.editDocBtn')}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    {/* Row 2: Generate (full width) */}
                                                                    <div className="px-3 py-3 bg-white dark:bg-zinc-900 [&>span]:w-full [&>button]:w-full">
                                                                        <TooltipButton hotkeyAction="GENERATE_DOCUMENT" onClick={handleGenerate} loading={loading} disabled={!isFormComplete || loading} fullWidth size="lg">
                                                                            {isRegenerating ? t('documents.regenerateDocs') : t('documents.generateDocuments')}
                                                                        </TooltipButton>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex flex-col gap-4 mt-1 px-1">
                                        {generatedDocs && !loading && (
                                            <>
                                                <div className="flex flex-col gap-3">
                                                    {templateDocs.map((doc, i) => {
                                                        const baseName = form.getFieldValue('documentName') || 'document';
                                                        const fileName = templateDocs.length > 1 ? `${baseName} - ${doc.title || `Document ${i + 1}`}` : baseName;
                                                        return (
                                                            <div key={doc.id || i} className="flex flex-col gap-1.5">
                                                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" title={fileName}>
                                                                    {doc.title || fileName}
                                                                </p>
                                                                <div className="flex items-center gap-2">
                                                                    <Btn variant="primary" size="sm" leftIcon={<IconDownload size={14} />} className="flex-1" onClick={() => handleDownloadGenerated(doc, fileName)}>
                                                                        {t('common.download')}
                                                                    </Btn>
                                                                    <Btn variant="tealLight" size="sm" leftIcon={<IconPrinter size={14} />} className="flex-1" onClick={() => handlePrint(doc, fileName)}>
                                                                        {t('common.print')}
                                                                    </Btn>
                                                                    <Btn variant="default" size="sm" leftIcon={<IconEdit size={14} />} className="flex-1"
                                                                        loading={docEditorBusyId === doc.id} onClick={() => openDocEditor(doc, i, fileName)}>
                                                                        {t('documents.docEditorBtn')}
                                                                    </Btn>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <Btn variant="violetLight" size="md" leftIcon={<IconShare size={16} />} onClick={handleShareGenerated}>
                                                        শেয়ার করুন
                                                    </Btn>
                                                    <Btn variant="light" size="md" leftIcon={<IconPencil size={16} />}
                                                        onClick={() => {
                                                            if (isGeneratedManualEdit) {
                                                                const seedMap = {};
                                                                (generatedDocs.generatedDocs || []).forEach((gd, i) => {
                                                                    const tDoc = templateDocs[i];
                                                                    if (tDoc && gd?.content) seedMap[tDoc.id] = sanitizeDocHtml(gd.content);
                                                                });
                                                                manualHtmlMapRef.current = seedMap;
                                                                setManualHtmlMap(seedMap);
                                                                const tsId = (templateSet || documentGroup?.templateSet)?.id;
                                                                Object.entries(seedMap).forEach(([docId, html]) => saveManualEditContent(tsId, docId, html));
                                                                saveManualEditMode(tsId, true);
                                                                setIsManualEditMode(true);
                                                                setIsEditMode(true);
                                                                setGeneratedDocs(null);
                                                                setGeneratedBlobs({});
                                                            } else {
                                                                setGeneratedDocs(null);
                                                                setGeneratedBlobs({});
                                                            }
                                                        }}
                                                    >
                                                        আবার এডিট করুন
                                                    </Btn>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </Form>
                        </div>

                        {/* Sticky footer */}
                        <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-white dark:bg-gray-900">
                            <div className="flex items-center justify-between">
                                {/* Left side */}
                                <div className="flex items-center gap-2">
                                    {(hasDraft || hasUnsavedChanges) && !generatedDocs && (
                                        <TipBtn
                                            onClick={handleClearDraft}
                                            tip={t('documents.clearDraft')}
                                            className="w-8 h-8 flex items-center justify-center rounded bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                        >
                                            <IconTrash size={16} />
                                        </TipBtn>
                                    )}
                                    {previewAvailable && !generatedDocs && isStampDoc && (
                                        <div className="flex items-center gap-2 ml-3">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('documents.stamp') || 'Stamp'}</span>
                                            <Switch
                                                checked={stampVisible}
                                                disabled={user?.settings?.stampPreviewEnabled === false}
                                                onChange={async (e) => {
                                                    const next = e.currentTarget.checked;
                                                    setStampVisible(next);
                                                    try { await updateSettings({ stampVisible: next }); } catch (_) {}
                                                }}
                                            />
                                            {stampVisible && (
                                                <select
                                                    value={String(stampDenomination)}
                                                    onChange={(e) => setStampDenomination(Number(e.target.value))}
                                                    className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 w-[80px]"
                                                >
                                                    <option value="50">৳৫০</option>
                                                    <option value="100">৳১০০</option>
                                                </select>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Right side */}
                                <div className="flex items-center gap-2">
                                    {generatedDocs && !isGeneratedManualEdit && (
                                        <TooltipButton hotkeyAction="PREV_PAGE" color="gray" onClick={handleBack}>
                                            {t('common.back')}
                                        </TooltipButton>
                                    )}
                                    {!generatedDocs ? (
                                        <>
                                            {isRegenerating && (
                                                <Btn variant="light" onClick={handleSeeDocs} loading={loading}>
                                                    {t('documents.seeDocs')}
                                                </Btn>
                                            )}
                                            {isOnLastStep && dynamicSteps.length === 0 && (
                                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                                    {previewAvailable && (
                                                        <Btn variant="default" size="sm" leftIcon={<IconPencil size={13} />} onClick={() => setManualEditConfirmOpen(true)}>
                                                            {t('documents.editDocBtn')}
                                                        </Btn>
                                                    )}
                                                    <TooltipButton hotkeyAction="GENERATE_DOCUMENT" onClick={handleGenerate} loading={loading} disabled={!isFormComplete || loading}>
                                                        {isRegenerating ? t('documents.regenerateDocs') : t('documents.generateDocuments')}
                                                    </TooltipButton>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <TooltipButton hotkeyAction="SUBMIT_FORM" onClick={handleDone}>
                                            {t('common.done')}
                                        </TooltipButton>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Modals live inside SheetContent so Radix DismissableLayer treats
                        clicks on them as "inside" the dialog — fixes pointer-event blocking */}
                    <LimitReachedModal opened={limitModalOpen} onClose={() => setLimitModalOpen(false)} isOperator={isOperator} />

                    {/* Manual edit confirm modal */}
                    <Modal opened={manualEditConfirmOpen} onClose={() => setManualEditConfirmOpen(false)} title={t('documents.manualEditConfirmTitle')}>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">{t('documents.manualEditConfirmMessage')}</p>
                        <div className="flex justify-end gap-2">
                            <Btn variant="default" onClick={() => setManualEditConfirmOpen(false)}>{t('documents.manualEditConfirmNo')}</Btn>
                            <Btn variant="primary" leftIcon={<IconPencil size={14} />} onClick={handleConfirmManualEdit}>{t('documents.manualEditConfirmYes')}</Btn>
                        </div>
                    </Modal>

                    {/* Destroy manual edit confirm modal */}
                    <Modal opened={destroyManualConfirmOpen} onClose={() => setDestroyManualConfirmOpen(false)} title={t('documents.destroyManualEditTitle')}>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">{t('documents.destroyManualEditMessage')}</p>
                        <div className="flex justify-end gap-2">
                            <Btn variant="default" onClick={() => setDestroyManualConfirmOpen(false)}>{t('documents.destroyManualEditCancel')}</Btn>
                            <Btn variant="red" onClick={handleDestroyManualEdit}>{t('documents.destroyManualEditConfirm')}</Btn>
                        </div>
                    </Modal>

                </SheetContent>
            </Sheet>

            {/* Document editor — beside the Sheet (not inside it) so its menus and typing are not trapped by the dialog. */}
            {docEditor && (
                <PdfEditorModal
                    open
                    file={docEditor.file}
                    initialObjects={docEditor.objects}
                    pageMargins={docEditor.pageMargins}
                    initialDirty={false}
                    documentMode
                    onClose={closeDocEditor}
                />
            )}
        </>
    );
}
